"use client"

import { useState } from "react"
import { Check, Copy } from "lucide-react"

interface EmbedType {
  id: string
  name: string
  slug: string
  categoryName: string
  categorySlug: string
  itemCount: number
}

export function EmbedConfigurator({ types }: { types: EmbedType[] }) {
  const [selected, setSelected] = useState(types[0]?.id || "")
  const [width, setWidth] = useState("100%")
  const [height, setHeight] = useState("600")
  const [copied, setCopied] = useState(false)

  const selectedType = types.find((t) => t.id === selected)

  const embedUrl = selectedType
    ? `https://playhigherlower.com/play/${selectedType.categorySlug}/${selectedType.slug}`
    : ""

  const embedCode = `<iframe src="${embedUrl}" width="${width}" height="${height}" style="border:none;border-radius:12px;" allow="autoplay" title="Higher Lower Game - ${selectedType?.name || ""}"></iframe>`

  const handleCopy = async () => {
    await navigator.clipboard.writeText(embedCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="space-y-6">
      {/* Game type selector */}
      <div>
        <label className="block text-sm text-zinc-400 mb-2">Choose a game</label>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-white focus:outline-none focus:border-zinc-600"
        >
          {types.map((type) => (
            <option key={type.id} value={type.id}>
              {type.categoryName}: {type.name} ({type.itemCount} items)
            </option>
          ))}
        </select>
      </div>

      {/* Size options */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-zinc-400 mb-2">Width</label>
          <input
            type="text"
            value={width}
            onChange={(e) => setWidth(e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-white placeholder:text-zinc-600 focus:outline-none focus:border-zinc-600"
          />
        </div>
        <div>
          <label className="block text-sm text-zinc-400 mb-2">Height (px)</label>
          <input
            type="text"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-white placeholder:text-zinc-600 focus:outline-none focus:border-zinc-600"
          />
        </div>
      </div>

      {/* Preview */}
      {selectedType && (
        <div>
          <label className="block text-sm text-zinc-400 mb-2">Preview</label>
          <div className="rounded-xl border border-zinc-800 overflow-hidden bg-zinc-900" style={{ height: `${Math.min(parseInt(height) || 400, 400)}px` }}>
            <iframe
              src={embedUrl}
              width="100%"
              height="100%"
              style={{ border: "none" }}
              title={`Preview - ${selectedType.name}`}
            />
          </div>
        </div>
      )}

      {/* Embed code */}
      <div>
        <label className="block text-sm text-zinc-400 mb-2">Embed code</label>
        <div className="relative">
          <pre className="p-4 rounded-xl bg-zinc-900 border border-zinc-800 text-sm text-emerald-400 overflow-x-auto font-mono">
            {embedCode}
          </pre>
          <button
            onClick={handleCopy}
            className={`absolute top-3 right-3 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              copied ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-800 text-zinc-400 hover:text-white"
            }`}
          >
            {copied ? (
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Copied</span>
            ) : (
              <span className="flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" /> Copy</span>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
